/**
 * Player settings store — mouse sensitivity + master volume.
 *
 * Persists to localStorage under `hupper.settings` — or
 * `hupper.settings.practice` on the free practice page, so tuning
 * mouse/volume in a practice match never touches the real (embedded)
 * game's settings and vice versa. The GameSettings gear panel writes back
 * on every change; the FirstPersonController reads the sensitivity
 * multiplier and `main.ts` applies the master volume to the PlayCanvas
 * SoundManager. Mirrors the crosshairStore pattern.
 */
import { isPracticeMode } from "../practiceMode";

export interface GameSettings {
  /** Mouse sensitivity multiplier (1.0 = the game's default feel). */
  sensitivity: number;
  /** Master volume 0..1 — scales every sound (1.0 = full). */
  masterVolume: number;
}

export const DEFAULT_SETTINGS: GameSettings = {
  sensitivity: 1.0,
  masterVolume: 1.0,
};

/** Slider bounds, shared by the UI and used to clamp loaded/saved values. */
export const SETTINGS_LIMITS = {
  sensitivity: { min: 0.1, max: 5.0, step: 0.05 },
  masterVolume: { min: 0, max: 1, step: 0.05 },
} as const;

const STORAGE_KEY = isPracticeMode ? "hupper.settings.practice" : "hupper.settings";

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

/** Coerce any partial/garbage input into a valid, clamped settings object. */
function sanitize(partial: Partial<GameSettings>): GameSettings {
  const s = { ...DEFAULT_SETTINGS, ...partial };
  return {
    sensitivity: clamp(s.sensitivity, SETTINGS_LIMITS.sensitivity.min, SETTINGS_LIMITS.sensitivity.max),
    masterVolume: clamp(s.masterVolume, SETTINGS_LIMITS.masterVolume.min, SETTINGS_LIMITS.masterVolume.max),
  };
}

export function loadSettings(): GameSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return sanitize(JSON.parse(raw) as Partial<GameSettings>);
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(cfg: GameSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
  } catch {
    /* localStorage unavailable — ignore */
  }
}

/** Subscribe to settings changes (same-tab). Returns an unsubscribe fn. */
const subscribers = new Set<(cfg: GameSettings) => void>();
export function subscribeSettings(cb: (cfg: GameSettings) => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

/** Persist + broadcast a settings change to every subscriber. */
export function notifySettingsChange(cfg: GameSettings): void {
  const clean = sanitize(cfg);
  saveSettings(clean);
  for (const cb of subscribers) cb(clean);
}
